/**
 * 東海道の国（その2） — 三河・遠江・駿河
 *
 * 盤面 6〜8 番。docs/01-ゲームデザイン.md §2.2 の表に従う。
 * 史実の書きぶりは docs/05-学習設計.md §4 と _reference.ts に揃えること。
 *
 * 石高の実値は慶長3年（1598年）の国別集計による。
 *   三河 約29.1万石／遠江 約25.5万石／駿河 約15.0万石
 */

import type { Province } from '../types';

/** 矢作川の平野を持つ国。地の利は騎馬。松平（徳川）氏の本国。 */
export const MIKAWA: Province = {
  id: 6,
  kind: 'province',
  name: '三河',
  reading: 'みかわ',
  region: 'tokai',
  kokudaka: 2,
  defense: 2,
  terrain: 'plain',
  historicalKokudaka: 29,
  note: {
    summary:
      '矢作川がつくる岡崎平野を中心とする国。三河湾では塩が作られた。松平（徳川）氏の本国で、尾張と駿河にはさまれた位置にある。',
    detail: {
      1: 'いまの愛知県の東がわにあった国です。矢作川のまわりに平野がひろがり、海ぞいでは塩がつくられました。岡崎城で生まれた徳川家康がこの国をまとめ、のちに天下をおさめることになります。',
      2: '現在の愛知県東部にあたる国。矢作川がつくる岡崎平野を中心とする西三河、豊川の流域の東三河、山がちな奥三河に分かれる。三河湾沿いの吉良などでは中世から製塩が行われ、内陸では後に木綿の産地としても知られた。戦国期には山間の松平郷から出た松平氏が岡崎を拠点としたが、尾張の織田氏と駿河の今川氏にはさまれて従属を繰り返した。1560年の桶狭間の戦いののち松平元康（のちの徳川家康）が自立し、1566年に徳川へ改姓している。1563年から翌年にかけては三河一向一揆が起こり、家臣団が二つに分かれて争った。',
      3: '現在の愛知県東部。矢作川流域の岡崎平野を中心とする西三河、豊川流域の東三河、山地の奥三河に分かれる。慶長3年（1598年）の集計では約29万石。三河湾岸の吉良荘では中世を通じて製塩（饗庭塩）が営まれ、内陸部では近世に木綿生産が広がった。室町幕府の守護は一色氏などが交替したが実効支配は弱く、松平郷から出た松平氏が岡崎周辺に勢力を築いた。松平元康は今川氏のもとで人質としての時期を過ごし、1560年の桶狭間の戦いののち岡崎へ戻って自立、1566年に徳川へ改姓して三河守に叙された。1563〜64年の三河一向一揆は、本證寺・上宮寺・勝鬘寺の守護不入権をめぐる対立を発端とし、浄土真宗の門徒であった家臣が一揆の側に立ったため、家臣団の分裂という形で展開した。1575年の長篠・設楽原の戦いも三河国内で行われている。1590年に徳川氏が関東へ移されると、三河は豊臣政権下の大名に分け与えられた。',
    },
    year: [1467, 1600],
    keywords: ['三河', '松平氏', '徳川家康', '岡崎', '三河一向一揆', '矢作川'],
    caveat:
      '長篠の戦いで鉄砲隊を三列に分けて交互に撃ったとする「三段撃ち」は、同時代の史料に裏づけがなく、後世に成立した話とみる研究者が多くいます。また徳川家康の生まれは和暦で天文11年12月にあたり、西暦に直すと1543年1月になるため、本によって1542年とも1543年とも書かれます。三河一向一揆についても、宗教対立というより寺内の不入特権をめぐる支配権の争いだったとする見方が有力です。',
    gameNote:
      '盤上の石高ランクは遊びやすさのために調整した相対値です。実際の三河は慶長3年（1598年）の集計で約29万石でした。盤上の地形は矢作川流域の平野を採って「平野」としていますが、実際の三河国には奥三河のような山がちな地域も含まれます。',
  },
};

/** 浜名湖と遠州灘に臨む国。地の利は湊（鉄砲／第I章は足軽）。 */
export const TOTOMI: Province = {
  id: 7,
  kind: 'province',
  name: '遠江',
  reading: 'とおとうみ',
  region: 'tokai',
  kokudaka: 2,
  defense: 3,
  terrain: 'coast',
  historicalKokudaka: 25.5,
  note: {
    summary:
      '国名は「都から遠い淡海」＝浜名湖に由来する。天竜川と大井川に区切られ、東海道が通る要地として今川・武田・徳川が争った。',
    detail: {
      1: 'いまの静岡県の西がわにあった国です。「とおとうみ」という名は、みやこから遠い湖、つまり浜名湖のことです。海と大きな川にかこまれ、東海道が通る大事な場所だったので、たびたび戦いの舞台になりました。',
      2: '現在の静岡県西部にあたる国。国名は都から遠い淡海（浜名湖）に由来する。東は大井川、西は浜名湖と遠州灘に区切られ、中央を天竜川が流れる。1498年の明応地震の津波で、浜名湖と海を隔てていた砂州が切れて「今切」と呼ばれる水路ができ、淡水湖だった浜名湖は海とつながった。天竜川の水運では上流の木材が運ばれ、沿岸では製塩や漁業が営まれた。戦国期は駿河の今川氏の支配下にあったが、1560年代末から徳川氏が進出し、掛川城・高天神城・浜松城をめぐって武田氏と長く争った。1572年には三方ヶ原で武田信玄が徳川・織田の軍を破っている。',
      3: '現在の静岡県西部。国名の「遠江」は都から遠い淡海、すなわち浜名湖を指す。慶長3年（1598年）の集計では約25.5万石。西の浜名湖から東の大井川までを占め、中央を天竜川が貫く。明応7年（1498年）の明応地震とその津波によって、浜名湖と遠州灘を隔てていた砂州が決壊して「今切」が開き、淡水湖であった浜名湖は汽水湖となった。掘削調査でも15世紀末の地形変化が確認されている。天竜川・大井川は東西交通の障害であると同時に、上流の木材を運ぶ水運路でもあり、沿岸部では製塩と漁業が生業として続いた。守護職をめぐって今川氏と斯波氏が長く争い、16世紀に今川氏の支配が固まったが、桶狭間の戦い後に動揺し、1568年からの徳川氏の侵攻で1569年に掛川城の今川氏真が退去した。以後は徳川氏と武田氏が高天神城などをめぐって攻防を繰り返し、徳川家康は1570年に本拠を浜松城へ移している。井伊谷を本拠とした井伊氏からは、女性の当主とされる井伊直虎の名が伝わる。',
    },
    year: [1467, 1600],
    keywords: ['遠江', '浜名湖', '天竜川', '今川氏', '徳川氏', '三方ヶ原'],
    caveat:
      '井伊直虎については、井伊直盛の娘で出家した次郎法師が当主となったとする伝えが広く知られていますが、2016年に紹介された江戸時代の記録『守安公書記』をもとに、直虎は今川氏の家臣・関口氏経の子である別人の男性だったとする説も出されました。同時代の史料ではないため異論もあり、研究者の間で決着していません。また1572年の三方ヶ原の戦いは和暦では元亀3年12月にあたり、西暦に直すと1573年1月になります。',
    gameNote:
      '盤上の石高ランクは遊びやすさのために調整した相対値です。実際の遠江は慶長3年（1598年）の集計で約25.5万石でした。盤上の地形は浜名湖・遠州灘に面する海の国として「湊」に分類していますが、実際には天竜川流域の平地や北部の山地も広く含みます。',
  },
};

/** 駿河湾に臨む国。地の利は湊（鉄砲／第I章は足軽）。湊と金山で銭を産む。 */
export const SURUGA: Province = {
  id: 8,
  kind: 'province',
  name: '駿河',
  reading: 'するが',
  region: 'tokai',
  kokudaka: 2,
  defense: 3,
  terrain: 'coast',
  historicalKokudaka: 15,
  yields: [{ res: 'zeni', n: 1 }],
  note: {
    summary:
      '駿河湾に臨み、東海道と海運が交わる国。平地は狭いが湊と金山を持ち、今川氏は府中（駿府）を拠点に分国法を定めた。',
    detail: {
      1: 'いまの静岡県のまん中あたりにあった国です。山が多くて米のとれる平地はせまいのですが、駿河湾の港と山の金山があり、今川氏はここを治めて豊かになりました。府中（駿府）には京のみやこの文化も伝わりました。',
      2: '現在の静岡県中部にあたる国。北に富士山や安倍の山地、南に駿河湾を持ち、平地が狭いため石高は多くない。しかし清水（江尻）などの湊と富士川の水運、安倍川上流の金山があり、今川氏はこれらの富で領国を支えた。1526年に今川氏親が分国法「今川仮名目録」を定め、1553年には義元が条文を加えている。氏親の死後には妻の寿桂尼が印判を用いて文書を出し、政務をとった時期があった。1560年に義元が桶狭間で敗死したのち今川氏は衰え、1568年に武田氏が駿河へ侵攻、翌年に今川氏真は駿河を失った。',
      3: '現在の静岡県中部。慶長3年（1598年）の集計では約15万石で、東海の国としては小さい。しかし駿河湾に臨む清水（江尻）の湊、富士川の水運、安倍川上流の梅ヶ島（安倍金山）が経済を支えた。『今川記』などによれば安倍郡の金は16世紀前半に幕府や公家へ贈られており、享禄年間（1528〜32年）に採掘が盛んだったと伝わる。守護今川氏は駿河・遠江を領し、府中（駿府）に館を構えた。1526年に今川氏親が制定した「今川仮名目録」は戦国大名の分国法として早い例で、1553年に義元が追加21条を定め、守護使不入などの旧来の権限を否定して領国の一元的な支配を進めた。氏親の没後、正室の寿桂尼は「帰」の字を刻んだ印判で文書を発給し、当主の代替わりの間の政務を担っており、大名家の政治に女性が関与した具体例として知られる。1560年の桶狭間の戦いで義元が敗死すると領国の統制は緩み、1568年に武田信玄が駿河へ侵攻、氏真は掛川へ退いて大名としての今川氏は終わった。武田氏の滅亡後は徳川氏の領国となり、のちに駿府は徳川家康の隠居の地となる。',
    },
    year: [1467, 1582],
    keywords: ['駿河', '今川氏', '駿府', '今川仮名目録', '寿桂尼', '安倍金山'],
    caveat:
      '1560年の桶狭間の戦いは、織田信長が迂回して背後から奇襲したという説が長く語られてきましたが、近年は正面から攻撃したとする説が有力とされています。また安倍金山の産出量を示す同時代の記録は乏しく、規模は正確にはわかっていません。梅ヶ島金山の開削を902年とする伝承もありますが、これは後世の言い伝えです。',
    gameNote:
      '盤上の石高ランクは遊びやすさのための相対値で、実際の駿河は慶長3年（1598年）の集計で約15万石と、石高だけなら大きな国ではありません。盤上で銭を産む設定にしているのは、清水（江尻）の湊と安倍金山による今川氏の財力を表したもので、石高と実際の収入が一致しないことを示すための調整です。',
  },
};
